import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { BookingCancelPolicy, CancelPolicy, HotelRoom, Supplement } from "src/store/apis/Hotels";
import { formatDate } from "src/utility";

dayjs.extend(customParseFormat);

export type RoomBlock = {
  name: string;
  promotion: string | null;
  mandatorySupplements: Supplement[];
  count: number;
};

export const MEAL_TYPE_LABELS: Record<string, string> = {
  room_only: "Room Only",
  breakfast: "Breakfast included",
  breakfast_for_1: "Breakfast for 1",
  breakfast_for_2: "Breakfast for 2",
  half_board: "Half Board",
  full_board: "Full Board",
  all_inclusive: "All Inclusive",
  all_inclusive_all: "All Inclusive",
  all_inclusive_all_meal: "All Inclusive",
  lunch: "Lunch included",
  dinner: "Dinner included",
  breakfast_lunch: "Breakfast and Lunch",
};

export function formatSupplementDescription(description: string | null): string {
  if (!description) return "";
  return description
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getMealTypeLabel(mealType: string): string {
  const key = mealType
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return MEAL_TYPE_LABELS[key] ?? formatSupplementDescription(mealType);
}

export function splitName(name: string): { title: string; details: string } {
  const parts = name.split(",");
  const title = parts[0].trim();
  const details = parts
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" & ");
  return { title, details };
}

export function getRoomBlocks(room: HotelRoom): RoomBlock[] {
  const rawNames = room.roomTypeName ?? [];
  const promos = room.roomPromotion ?? [];
  const sups = room.supplements ?? [];

  if (!rawNames.length) {
    return [{ name: "Standard Room", promotion: null, mandatorySupplements: [], count: 1 }];
  }

  const blocks: RoomBlock[] = [];
  rawNames.forEach((name, i) => {
    const promotion = promos[i] ?? null;
    const roomSupplements = sups.filter((s) => s.index === i + 1);
    const mandatorySupplements = getMandatorySupplements(roomSupplements);
    const last = blocks[blocks.length - 1];
    const same = last && last.name === name;
    if (same) last.count += 1;
    else blocks.push({ name, promotion, mandatorySupplements, count: 1 });
  });
  return blocks;
}

export function getInclusionTags(room: { inclusion: string | null }): string[] {
  return (room.inclusion ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function getMandatorySupplements(sups: Supplement[]): Supplement[] {
  return sups.filter((s) => s.type === "AtProperty" && s.description);
}

export type PaidCancellationPolicy = {
  fromDate: string;
  chargeType: string | null;
  cancellationCharge: number;
  currency: string | null;
};

export type CancellationInfo = {
  refundable: boolean;
  freeCancelUntil: string | null;
  hasFreeCancellation: boolean;
  paidPolicies: PaidCancellationPolicy[];
};

export function getCancellationInfo(policies: (CancelPolicy | BookingCancelPolicy)[], isRefundable: boolean): CancellationInfo {
  const datedPolicies = policies
    .filter((p) => p.fromDate)
    .map((p) => ({ policy: p, date: dayjs(p.fromDate!, "DD-MM-YYYY HH:mm:ss") }))
    .filter(({ date }) => date.isValid())
    .sort((a, b) => a.date.valueOf() - b.date.valueOf());

  const paidPolicies = datedPolicies.filter(({ policy }) => policy.cancellationCharge > 0);
  const freePolicies = datedPolicies.filter(({ policy }) => policy.cancellationCharge <= 0);

  const earliestPaid = paidPolicies[0];
  const freeCancelUntil = earliestPaid ? formatDate(earliestPaid.date.subtract(1, "day"), "MMM D") : null;
  const hasFreeCancellation = Boolean(freeCancelUntil) || freePolicies.length > 0;
  return {
    refundable: isRefundable,
    freeCancelUntil,
    hasFreeCancellation,
    paidPolicies: paidPolicies.map(({ policy, date }) => ({
      fromDate: formatDate(date, "MMM D"),
      chargeType: policy.chargeType,
      cancellationCharge: policy.convertedCancellationCharge ?? policy.cancellationCharge,
      currency: policy.convertedCurrency ?? null,
    })),
  };
}
